/**
 * ProjectNewsFeed - Project-level Facebook-style news feed
 * Posts scoped to the project. Task/epic comments with includeInProjectFeed also appear here.
 */
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { FolderKanban } from 'lucide-react';
import { useApp } from '../providers/AppProvider';
import { NewsFeedComposer, NewsFeedPostCard } from '../components/NewsFeedShared';
import { useNewsFeed } from '../hooks/useNewsFeed';
import { WIDGET_MUTED_TEXT_CLASS, WIDGET_PANEL_CLASS } from '../constants/widgetTheme';

export default function ProjectNewsFeed() {
  const { projectId } = useParams<{ projectId: string }>();
  const { state } = useApp();

  const currentUser = state?.currentUser;
  const org = state?.currentOrganization;
  const project = (state?.projects ?? []).find((p) => p.id === projectId);
  const statePosts = useMemo(
    () => (state?.newsFeedPosts ?? []).filter((post) => post.projectId === projectId && post.scope === 'project'),
    [projectId, state?.newsFeedPosts]
  );
  const {
    posts,
    loading,
    createPost,
    editPost,
    deletePost,
    commentOnPost,
    reactToPost,
  } = useNewsFeed({
    organizationId: org?.id,
    projectId,
    currentUser,
    fallbackPosts: statePosts,
  });

  if (!currentUser || !org || !project) {
    return (
      <div className={`flex h-64 items-center justify-center ${WIDGET_MUTED_TEXT_CLASS}`}>
        Loading project feed...
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-3 mb-1">
          {project.logo ? (
            <img src={project.logo} alt={project.name} className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <FolderKanban className="h-5 w-5 text-primary" />
          )}
          <h1 className="text-2xl font-semibold text-foreground">Feeds</h1>
        </div>
        <p className={`ml-9 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>{posts.length} post{posts.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Composer */}
      <div className="mb-6">
        <NewsFeedComposer
          currentUserName={currentUser.name}
          currentUserAvatar={currentUser.avatar}
          fallbackAvatarSrc={project.logo}
          placeholder="Share an update with the project team..."
          onSubmit={createPost}
        />
      </div>

      {/* Feed */}
      <div className="space-y-4">
        {loading ? (
          <div className={`py-12 text-center ${WIDGET_MUTED_TEXT_CLASS}`}>Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className={`${WIDGET_PANEL_CLASS} py-16 text-center`}>
            <FolderKanban className="mx-auto mb-3 h-12 w-12 text-muted-foreground/60" />
            <p className="font-medium text-foreground">No posts yet</p>
            <p className={`mt-1 text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
              Share project updates, or mark task comments to appear here.
            </p>
          </div>
        ) : (
          posts.map((post) => (
            <NewsFeedPostCard
              key={post.id}
              post={post}
              currentUserId={currentUser.id}
              currentUserName={currentUser.name}
              currentUserAvatar={currentUser.avatar}
              onEdit={editPost}
              onDelete={deletePost}
              onComment={commentOnPost}
              onReact={reactToPost}
            />
          ))
        )}
      </div>
    </div>
  );
}
